import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { formatCurrency } from "utils/helpers/accounting";
import { ROUTES } from "utils";
import { useNavigate } from "react-router-dom";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
const todayString = formatDate(today);

type ARQuery = {
  from_date: string;
  to_date: string;
  partner_id?: string;
  report_type: "RECEIVABLE" | "PAYABLE";
};

const RpAccountReceivable: React.FC = () => {
  const navigate = useNavigate();
  const { fetchRpAccountReceivable } = useReport();
  const [filters, setFilters] = useState<ARQuery>({
    from_date: firstOfMonth,
    to_date: todayString,
    report_type: "RECEIVABLE",
  });
  const [submitted, setSubmitted] = useState<ARQuery | null>(null);

  const handleFilterChange = useCallback((key: keyof ARQuery, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) return;
      setSubmitted(filters);
      fetchRpAccountReceivable.mutate({
        from_date: filters.from_date,
        to_date: filters.to_date,
        type: filters.report_type,
        partner_id: filters.partner_id ? Number(filters.partner_id) : undefined,
      });
    },
    [fetchRpAccountReceivable, filters],
  );

  const allRecords = (fetchRpAccountReceivable.data?.data ?? []) as any[];

  const columns = useMemo(
    () => [
      {
        headerName: "STT",
        valueGetter: (p: any) => (p.node?.rowIndex ?? 0) + 1,
        pinned: "left",
        minWidth: 60,
        maxWidth: 70,
        cellClass: "text-center text-gray-500 text-sm",
      },
      {
        headerName: "Mã khách hàng...",
        field: "partner_code",
        pinned: "left",
        minWidth: 130,
      },
      {
        headerName: "Khách hàng, NCC liên quan",
        field: "partner_name",
        minWidth: 220,
        flex: 1,
      },
      {
        headerName: "Số dư đầu kỳ",
        children: [
          {
            headerName: "Nợ",
            field: "opening_debit",
            minWidth: 120,
            valueFormatter: (p: any) => p.value != null && p.value !== 0 ? formatCurrency(Number(p.value)) : "",
            cellClass: "text-right text-red-600",
          },
          {
            headerName: "Có",
            field: "opening_credit",
            minWidth: 120,
            valueFormatter: (p: any) => p.value != null && p.value !== 0 ? formatCurrency(Number(p.value)) : "",
            cellClass: "text-right text-green-700",
          },
        ],
      },
      {
        headerName: "Số phát sinh trong kỳ",
        children: [
          {
            headerName: "Nợ",
            field: "period_debit",
            minWidth: 120,
            valueFormatter: (p: any) => p.value != null && p.value !== 0 ? formatCurrency(Number(p.value)) : "",
            cellClass: "text-right text-red-600",
          },
          {
            headerName: "Có",
            field: "period_credit",
            minWidth: 120,
            valueFormatter: (p: any) => p.value != null && p.value !== 0 ? formatCurrency(Number(p.value)) : "",
            cellClass: "text-right text-green-700",
          },
        ],
      },
      {
        headerName: "Số dư cuối kỳ",
        children: [
          {
            headerName: "Nợ",
            field: "closing_debit",
            minWidth: 130,
            valueFormatter: (p: any) => p.value != null && p.value !== 0 ? formatCurrency(Number(p.value)) : "",
            cellClass: "text-right font-semibold text-red-700",
          },
          {
            headerName: "Có",
            field: "closing_credit",
            minWidth: 130,
            valueFormatter: (p: any) => p.value != null && p.value !== 0 ? formatCurrency(Number(p.value)) : "",
            cellClass: "text-right font-semibold text-green-700",
          },
        ],
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: "agTextColumnFilter", floatingFilter: true, minWidth: 100 }),
    [],
  );

  const isLoading = fetchRpAccountReceivable.isPending;

  return (
    <div className="p-6 space-y-5">
      {isLoading && <LoadingState message="Đang tải báo cáo" description="Vui lòng chờ trong giây lát." />}

      <nav className="text-sm text-blue-600 flex items-center gap-1 flex-wrap">
        <span className="cursor-pointer hover:underline font-medium" onClick={() => navigate(ROUTES.PRIVATE_ROOT.DASHBOARD)}>BÁO CÁO</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-500 cursor-pointer hover:underline">Sổ sách, báo cáo quản trị</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-700 font-medium">Báo cáo công nợ</span>
      </nav>

      <h1 className="text-xl font-bold text-gray-900">Báo cáo tổng hợp công nợ</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Từ ngày <span className="text-red-500">*</span></label>
            <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" required />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Đến ngày <span className="text-red-500">*</span></label>
            <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" required />
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium text-gray-700">Khách hàng, NCC liên quan</label>
            <input type="text" value={filters.partner_id || ""} onChange={(e) => handleFilterChange("partner_id", e.target.value || undefined)}
              placeholder="Lựa chọn..."
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="space-y-1 flex-1 max-w-xs">
            <label className="text-sm font-medium text-gray-700">Loại báo cáo</label>
            <div className="relative">
              <select
                value={filters.report_type}
                onChange={(e) => handleFilterChange("report_type", e.target.value as "RECEIVABLE" | "PAYABLE")}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none appearance-none pr-8"
              >
                <option value="RECEIVABLE">Khoản phải thu</option>
                <option value="PAYABLE">Khoản phải trả</option>
              </select>
              {filters.report_type && (
                <button type="button" onClick={() => handleFilterChange("report_type", "RECEIVABLE")}
                  className="absolute right-7 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button type="submit" disabled={isLoading}
              className="rounded border border-blue-600 px-4 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition">
              Xem số liệu
            </button>
            <button type="button" onClick={handleSubmit} disabled={isLoading}
              className="rounded border border-blue-600 px-4 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition">
              Xem báo cáo
            </button>
            <button type="button"
              className="rounded border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
              Xuất file Excel
            </button>
          </div>
        </div>
      </form>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {submitted ? (
          <>
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">
                {allRecords.length} bản ghi · {submitted.from_date} → {submitted.to_date}
              </h2>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${filters.report_type === "RECEIVABLE" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                {filters.report_type === "RECEIVABLE" ? "Khoản phải thu" : "Khoản phải trả"}
              </span>
            </div>
            <AgGridTable rows={allRecords} columns={columns as any} defaultColDef={defaultColDef} pagination={true} autoHeight />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <svg className="w-12 h-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-sm">Nhấn <strong>Xem số liệu</strong> hoặc <strong>Xem báo cáo</strong> để tải dữ liệu</p>
          </div>
        )}
      </div>

      {submitted && allRecords.length === 0 && !isLoading && (
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-gray-400">
          <p>Không có dữ liệu trong khoảng thời gian đã chọn</p>
        </div>
      )}
    </div>
  );
};

export default RpAccountReceivable;
